/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useMemo, useCallback, useRef, useEffect } from '@web-stories-wp/react';
import {
  createSolid,
  hasGradient,
  isPatternEqual,
} from '@web-stories-wp/patterns';
import { useGlobalKeyDownEffect } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import useRichText from '../../../richText/useRichText';
import {
  ensureHTMLUnorderedList,
  getHTMLFormatters,
  getHTMLInfo,
  removeHTMLListFormatting,
} from '../../../richText/htmlManipulation';
import {
  getPrefixStyleForCharacter,
  getPrefixStylesInSelection,
} from '../../../richText/styleManipulation';
import { getInlineStyleAtSelectionStart } from '../../../richText/draftUtils';
import { COLOR, COLOR_GRADIENT, NONE } from '../../../richText/customConstants';
import { MULTIPLE_VALUE } from '../../../../constants';
import { useCanvas } from '../../../../app';

/**
 * Equality function for *primitives and color patterns* only.
 *
 * @param {any} a  First value to compare
 * @param {any} b  Second value to compare
 * @return {boolean} True if equal
 */
function isEqual(a, b) {
  // patterns are truthy objects with either a type or a color attribute.
  // Note: `null` is a falsy object, that would cause an error if first
  // check is removed.
  const isAPattern = a && typeof a === 'object' && (a.type || a.color);
  const isBPattern = b && typeof b === 'object' && (b.type || b.color);

  return isAPattern && isBPattern ? isPatternEqual(a, b) : a === b;
}

/**
 * A function to gather the text info for multiple elements into a single
 * one.
 *
 * The text info object contains a number of values that can be either
 * primitives or a color object.
 *
 * If any two objects for the same key have different values, return
 * `MULTIPLE_VALUE`. Uses `isEqual` to determine this equality.`
 *
 * @param {Object} reduced  Currently reduced object from previous elements
 * - will be empty for first object
 * @param {Object} info  Info about current object
 * @return {Object} Combination of object as described.
 */
function reduceWithMultiple(reduced, info) {
  return Object.fromEntries(
    Object.keys(info).map((key) => {
      const wasMultiple = reduced[key] === MULTIPLE_VALUE;
      const hadValue = typeof reduced[key] !== 'undefined';
      const areDifferent = hadValue && !isEqual(reduced[key], info[key]);
      if (wasMultiple || areDifferent) {
        return [key, MULTIPLE_VALUE];
      }
      return [key, info[key]];
    })
  );
}

function getGradientColorUpdate(color, htmlFormatters) {
  const resetColor = createSolid(0, 0, 0);
  return ({ content, textAlign }) => ({
    content: htmlFormatters.setColor(content, resetColor),
    ...(textAlign === 'justify' ? { textAlign: 'left' } : {}),
    lastUpdatedBy: 'user',
    color,
  });
}

function useRichTextFormatting(selectedElements, pushUpdate) {
  const {
    state: { hasCurrentEditor, selectionInfo, editorState },
    actions: { selectionActions },
  } = useRichText();

  const { clearEditing } = useCanvas(({ actions: { clearEditing } }) => ({
    clearEditing,
  }));

  const gradientColor = useMemo(() => {
    if (selectedElements.length === 0) {
      return null;
    }

    const gradientColors = selectedElements
      .map((element) => element.color)
      .filter(hasGradient);

    if (gradientColors.length === 0) {
      return null;
    }

    if (gradientColors.length !== selectedElements.length) {
      return MULTIPLE_VALUE;
    }

    const [firstGradient, ...restGradients] = gradientColors;
    const allSame = restGradients.every((color) =>
      isPatternEqual(color, firstGradient)
    );

    return allSame ? firstGradient : MULTIPLE_VALUE;
  }, [selectedElements]);

  const hasInlineColorInSelection = useMemo(() => {
    if (!editorState) {
      return false;
    }

    const selection = editorState.getSelection();
    const inlineStyleOverride = editorState.getInlineStyleOverride();

    if (selection.isCollapsed() && !inlineStyleOverride) {
      const styleSet = getInlineStyleAtSelectionStart(editorState);
      if (!styleSet) {
        return false;
      }

      const gradientStyle = getPrefixStyleForCharacter(
        styleSet,
        COLOR_GRADIENT
      );
      const colorStyle = getPrefixStyleForCharacter(styleSet, COLOR);

      return gradientStyle !== NONE || colorStyle !== NONE;
    }

    const gradientStyles = getPrefixStylesInSelection(
      editorState,
      COLOR_GRADIENT
    );
    const colorStyles = getPrefixStylesInSelection(editorState, COLOR);
    const hasGradientStyle = gradientStyles.some((style) => style !== NONE);
    const hasSolidStyle = colorStyles.some((style) => style !== NONE);

    return hasGradientStyle || hasSolidStyle;
  }, [editorState]);

  const textInfo = useMemo(() => {
    const baseInfo = hasCurrentEditor
      ? selectionInfo
      : selectedElements
          .map(({ content }) => content)
          .map(getHTMLInfo)
          .reduce(reduceWithMultiple, {});

    if (gradientColor === null) {
      return baseInfo;
    }

    if (hasCurrentEditor && hasInlineColorInSelection) {
      return baseInfo;
    }

    return {
      ...baseInfo,
      color: gradientColor,
    };
  }, [
    hasCurrentEditor,
    selectionInfo,
    selectedElements,
    gradientColor,
    hasInlineColorInSelection,
  ]);

  const push = useCallback(
    (updater, ...args) =>
      pushUpdate(
        ({ content }) => ({
          content: updater(content, ...args),
          lastUpdatedBy: 'user',
        }),
        true
      ),
    [pushUpdate]
  );

  const queuedPushRef = useRef(null);
  const queuedUpdateRef = useRef(null);
  const queuePush = useCallback(
    (...args) => {
      queuedPushRef.current = args;
    },
    [queuedPushRef]
  );
  const queueUpdate = useCallback(
    (update) => {
      queuedUpdateRef.current = update;
    },
    [queuedUpdateRef]
  );

  // After editing closes or selected elements refresh, run any queued update or push.
  useEffect(() => {
    if (hasCurrentEditor) {
      return;
    }

    const update = queuedUpdateRef.current;
    if (update) {
      pushUpdate(update, true);
      queuedUpdateRef.current = null;
    }

    const pushArgs = queuedPushRef.current;
    if (pushArgs) {
      push(...pushArgs);
      queuedPushRef.current = null;
    }
  }, [
    hasCurrentEditor,
    selectedElements,
    queuedPushRef,
    queuedUpdateRef,
    push,
    pushUpdate,
  ]);

  const hasActiveRange = Boolean(
    hasCurrentEditor && editorState && !editorState.getSelection().isCollapsed()
  );

  const handlers = useMemo(() => {
    const htmlFormatters = getHTMLFormatters();

    if (hasCurrentEditor) {
      return {
        // This particular function ignores the flag argument.
        // Bold for inline selection has its own logic for
        // determining proper resulting bold weight
        handleClickBold: () => selectionActions.toggleBoldInSelection(),
        // All these keep their arguments:
        handleSelectFontWeight: selectionActions.setFontWeightInSelection,
        handleClickItalic: selectionActions.toggleItalicInSelection,
        handleClickUnderline: selectionActions.toggleUnderlineInSelection,
        handleClickUppercase: selectionActions.toggleUppercaseInSelection,
        handleApplyUnorderedList:
          selectionActions.toggleUnorderedListInSelection,
        handleRemoveList: selectionActions.removeListInSelection,
        handleSetLetterSpacing: selectionActions.setLetterSpacingInSelection,
        handleSetFontFamily: selectionActions.setFontFamilyInSelection,
        handleSetFontSize: selectionActions.setFontSizeInSelection,
        handleSetLineHeight: selectionActions.setLineHeightInSelection,
        handleApplyTextStylePreset:
          selectionActions.applyTextStylePresetInSelection,
        handleSetColor: async (color) => {
          if (hasGradient(color) && !hasActiveRange) {
            queueUpdate(getGradientColorUpdate(color, htmlFormatters));
            await clearEditing();
            return;
          }

          selectionActions.setColorInSelection(color);
        },
        // when editing, resetting font weight needs to save before resetting
        handleResetFontWeight: async (weight) => {
          // queue push until selectedElements are saved and content is updated
          queuePush(htmlFormatters.setFontWeight, weight);
          // clear editing to save any pending updates
          await clearEditing();
        },
      };
    }

    return {
      handleClickBold: (flag) => push(htmlFormatters.toggleBold, flag),
      handleSelectFontWeight: (weight) =>
        push(htmlFormatters.setFontWeight, weight),
      handleClickItalic: (flag) => push(htmlFormatters.toggleItalic, flag),
      handleClickUnderline: (flag) =>
        push(htmlFormatters.toggleUnderline, flag),
      handleClickUppercase: (flag) =>
        push(htmlFormatters.toggleUppercase, flag),
      handleApplyUnorderedList: () => push(ensureHTMLUnorderedList),
      handleRemoveList: () => push(removeHTMLListFormatting),
      handleSetLetterSpacing: (letterSpacing) =>
        push(htmlFormatters.setLetterSpacing, letterSpacing),
      handleSetFontFamily: null,
      handleSetFontSize: null,
      handleSetLineHeight: (lineHeight, commit = true) =>
        pushUpdate(
          ({ content }) => ({
            ...(/<[^>]*\bdata-st-line-height\s*=/i.test(content)
              ? {
                  content: htmlFormatters.clearLineHeight(content),
                }
              : {}),
            lineHeight,
            lastUpdatedBy: 'user',
          }),
          commit
        ),
      handleApplyTextStylePreset: null,
      handleSetColor: (color) => {
        if (hasGradient(color)) {
          const update = getGradientColorUpdate(color, htmlFormatters);
          const isPaintOnly = selectedElements.every(
            (element) =>
              hasGradient(element.color) &&
              element.textAlign !== 'justify' &&
              update(element).content === element.content
          );
          pushUpdate(update, true, { skipPresubmit: isPaintOnly });
          return;
        }

        pushUpdate(
          ({ content }) => ({
            content: htmlFormatters.setColor(content, color),
            lastUpdatedBy: 'user',
            color: null,
          }),
          true
        );
      },
      handleResetFontWeight: (weight) =>
        push(htmlFormatters.setFontWeight, weight),
    };
  }, [
    hasCurrentEditor,
    selectedElements,
    hasActiveRange,
    selectionActions,
    push,
    pushUpdate,
    clearEditing,
    queuePush,
    queueUpdate,
  ]);

  const hasText = useMemo(() => {
    const texts = selectedElements.filter(({ type }) => type === 'text');

    return texts.length > 0;
  }, [selectedElements]);

  useGlobalKeyDownEffect(
    { key: ['mod+b', 'mod+u', 'mod+i'] },
    ({ key }) => {
      if (!hasText) {
        return;
      }
      switch (key) {
        case 'b':
          handlers.handleClickBold();
          break;
        case 'i':
          handlers.handleClickItalic();
          break;
        case 'u':
          handlers.handleClickUnderline();
          break;
        default:
          break;
      }
    },
    [handlers, hasText]
  );

  return {
    textInfo,
    handlers,
    isEditingMixedTypography: hasCurrentEditor,
    hasActiveRange,
  };
}

export default useRichTextFormatting;
